import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Banknote, CreditCard, Minus, Plus, Receipt, Send, Smartphone, StickyNote, Utensils, Wallet } from "lucide-react";
import { API, GST_RATE, cartLineKey, describeChoice, fmtErr, money } from "../lib/pos";
import { printBill, printOrderKOTs } from "../lib/print";
import { enqueueOrder } from "../lib/offline";
import { ItemConfigurator } from "../components/ItemConfigurator";
import { SplitModal } from "../components/SplitModal";
import CashTenderModal from "../components/CashTenderModal";
import { ManagerPinModal } from "../components/ManagerPinModal";

/* ---------- POS (manager only) ---------- */
export function Pos({ menu, stations, billSettings, refresh, setView, pendingDraft, clearPendingDraft }) {
  const [mode, setMode] = useState("floor"); // 'floor' = dine-in (default), 'counter' = takeaway. Tables removed — no table pick.
  const [cart, setCart] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [discountPercent, setDiscountPercent] = useState(0); // 0..100
  const [pinAsk, setPinAsk] = useState(null); // { percent } while asking
  const [discountReason, setDiscountReason] = useState("");
  const [taxMode] = useState("intra"); // Intrastate always (CGST + SGST)
  const [skipKOT, setSkipKOT] = useState(false);
  const [skipBill, setSkipBill] = useState(false);
  const [pendingCollect, setPendingCollect] = useState(null); // order awaiting payment
  const [splitOpen, setSplitOpen] = useState(false);
  const [cashOpen, setCashOpen] = useState(false); // cash-tender step
  const [configuring, setConfiguring] = useState(null); // menu item being configured
  const [noteFor, setNoteFor] = useState(null); // cartKey being edited
  const [noteDraft, setNoteDraft] = useState("");

  // Table management removed — no table state to keep in sync with mode.

  const [pageSize, setPageSize] = useState(20);
  const [topSelling, setTopSelling] = useState([]);
  useEffect(() => {
    // Fetch the 10 most-selling items for the shortcut bar. Refreshed once per Pos mount — no need to poll.
    axios.get(`${API}/menu/top-selling?limit=10`).then((r) => setTopSelling(r.data || [])).catch(() => {});
  }, []);

  // Hydrate cart from a customer draft ticket if one was handed to us.
  useEffect(() => {
    if (!pendingDraft) return;
    const items = (pendingDraft.items || []).map((it, idx) => ({
      cartKey: `draft-${pendingDraft.draft_number}-${idx}-${it.menu_id}`,
      menu_id: it.menu_id,
      name: it.name,
      category: it.category,
      price: it.price,
      quantity: it.quantity,
      variant: null,
      addons: [],
      note: it.note || "",
    }));
    setCart(items);
    setMode(pendingDraft.order_type === "table" ? "floor" : "counter");
    setDiscountPercent(0);
    setDiscountReason("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDraft?.draft_number]);
  const cats = useMemo(() => ["All", ...Array.from(new Set(menu.filter((x) => x.effective_active !== false).map((x) => x.category)))], [menu]);
  const q = search.trim().toLowerCase();
  const filteredAll = menu.filter((x) => {
    if (x.effective_active === false) return false; // hidden — station/category/item is disabled
    // When a search query is active, ignore the category filter and search across the entire menu.
    // Category chips only apply when the search box is empty.
    if (!q && category !== "All" && x.category !== category) return false;
    if (!q) return true;
    return (
      x.name.toLowerCase().includes(q) ||
      (x.description || "").toLowerCase().includes(q) ||
      x.category.toLowerCase().includes(q)
    );
  });
  const totalPages = Math.max(1, Math.ceil(filteredAll.length / pageSize));
  useEffect(() => { setPage(1); }, [category, q, pageSize]);
  const currentPage = Math.min(page, totalPages);
  const filtered = filteredAll.slice((currentPage - 1) * pageSize, currentPage * pageSize);
  const subtotal = cart.reduce((a, x) => a + x.price * x.quantity, 0);
  // Percentage discount: computed slab × subtotal. Rounded to 2dp for display consistency with backend.
  const discount = Math.round(subtotal * (Number(discountPercent) || 0) / 100 * 100) / 100;
  const taxable = Math.max(0, subtotal - discount);
  const gstPreview = Math.round(taxable * GST_RATE * 100) / 100;
  const rawTotal = taxable + gstPreview;
  const rounded = Math.floor(rawTotal + 0.5);
  const roundingPreview = Math.round((rounded - rawTotal) * 100) / 100;
  const total = rounded;

  const openItem = (item) => {
    // ALL items now open the configurator — even simple ones show quantity + note fields.
    setConfiguring(item);
  };
  const addLine = (item, variant, addons, unitPrice, quantity = 1, note = null) => {
    const key = cartLineKey(item.id, variant, addons);
    setCart((c) => {
      const existing = c.find((x) => x.cartKey === key);
      if (existing) {
        return c.map((x) => (x.cartKey === key
          ? { ...x, quantity: x.quantity + quantity, note: note || x.note || null }
          : x));
      }
      return [...c, {
        cartKey: key,
        menu_id: item.id,
        name: item.name,
        category: item.category,
        price: unitPrice,
        quantity: quantity,
        variant: variant ? { id: variant.id, name: variant.name, price_delta: variant.price_delta || 0, sub_variants: (variant.sub_variants || []).map((sv) => ({ id: sv.id, name: sv.name, price_delta: sv.price_delta || 0 })) } : null,
        addons: addons || [],
        note: note || null,
      }];
    });
  };
  const bumpLine = (key, delta) => setCart((c) => c.flatMap((x) => {
    if (x.cartKey !== key) return [x];
    const q = x.quantity + delta;
    return q > 0 ? [{ ...x, quantity: q }] : [];
  }));

  const [submitting, setSubmitting] = useState(false);
  const submitOrder = async () => {
    if (submitting) return null;
    if (!cart.length) return null;
    if (discountPercent > 0 && !discountReason.trim()) {
      alert("Please select a discount slab (reason auto-fills).");
      return null;
    }
    setSubmitting(true);
    const payload = {
      order_type: mode === "counter" ? "takeaway" : "table",
      // table intentionally omitted — tables are disabled in the current build.
      items: cart.map((x) => ({
        menu_id: x.menu_id, name: x.name, price: x.price, quantity: x.quantity, category: x.category,
        variant: x.variant, addons: x.addons, note: x.note || null,
      })),
      discount,
      discount_percent: discountPercent || 0,
      discount_reason: discountPercent > 0 ? (discountReason.trim() || `${discountPercent}% discount`) : null,
      tax_mode: taxMode,
    };
    // If we're offline, don't even try the network — queue locally so the cook still gets a KOT.
    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      const rec = enqueueOrder(payload);
      setCart([]);
      setDiscountPercent(0);
      setDiscountReason("");
      setSubmitting(false);
      // Compose a receipt-shaped stub so the KOT/Bill print helpers still work.
      return {
        ...payload,
        id: rec.local_id,
        order_number: rec.local_order_number,
        token_number: rec.local_token_number,
        status: "queued_offline",
        subtotal: cart.reduce((s, x) => s + x.price * x.quantity, 0),
        total: 0, // GST recomputed server-side; local bill omits totals row for clarity
        cgst: 0, sgst: 0, igst: 0, tax: 0, service: 0, rounding: 0,
        created_at: new Date().toISOString(),
        _offline: true,
      };
    }
    try {
      const r = await axios.post(`${API}/orders`, payload);
      // reset POS state
      setCart([]);
      setDiscountPercent(0);
      setDiscountReason("");
      // If we came from a customer draft, link it to the new order and stop the expiry timer.
      if (pendingDraft?.draft_number && r.data?.id) {
        try {
          await axios.post(`${API}/drafts/${pendingDraft.draft_number}/mark-converted`, {
            order_id: r.data.id,
          });
        } catch (_) { /* non-fatal — the order is already placed */ }
        clearPendingDraft?.();
      }
      refresh();
      return r.data;
    } catch (e) {
      // Network failed mid-request — save to queue as a safety net.
      if (e && e.message && /Network Error|timeout|Failed to fetch/i.test(e.message)) {
        const rec = enqueueOrder(payload);
        setCart([]);
        setDiscountPercent(0);
        setDiscountReason("");
        alert(`Network dropped — order queued locally (${rec.local_id}). Will sync when online.`);
        return {
          ...payload, id: rec.local_id, order_number: rec.local_order_number,
          token_number: rec.local_token_number, status: "queued_offline", total: 0,
          cgst: 0, sgst: 0, igst: 0, tax: 0, service: 0, rounding: 0,
          created_at: new Date().toISOString(), _offline: true,
        };
      }
      alert(fmtErr(e));
      return null;
    }
    finally { setSubmitting(false); }
  };
  const placeOrder = async () => {
    const order = await submitOrder();
    if (!order) return;
    if (!skipKOT) printOrderKOTs(order, stations || []);
    setSkipKOT(false); setSkipBill(false);
    if (setView) setView("kitchen");
  };
  const collectPayment = () => {
    // DO NOT place the order yet. Open the payment picker first — the order is only
    // created once the cashier confirms which method (Cash / Card / UPI / Split) actually
    // hit the till. This mirrors how the guest hands over money before the ticket is punched.
    if (!cart.length) return;
    setPendingCollect({
      pre: true,
      total,
      subtotal,
      order_type: mode === "counter" ? "takeaway" : "table",
    });
  };
  const finishCollect = async (method, splits) => {
    if (!pendingCollect) return;
    // 1) Place the order NOW that the cashier has confirmed a payment method.
    const order = await submitOrder();
    if (!order) return; // submitOrder alerts on error; keep the picker open so cashier can retry
    try {
      const body = { order_id: order.id, amount: order.total, method };
      if (splits) body.splits = splits;
      const r = await axios.post(`${API}/payments`, body);
      // Use the freshly-updated order returned by the payment endpoint so
      // status / paid_at reflect exactly what the backend recorded.
      const fresh = r.data?.order || { ...order, payment_method: method, payment_splits: splits || null };
      if (!skipBill) printBill(fresh, stations || [], { settings: billSettings, isDuplicate: false });
      if (!skipKOT) printOrderKOTs(fresh, stations || []);
      setSkipKOT(false); setSkipBill(false);
      setPendingCollect(null); setSplitOpen(false);
      refresh();
      if (setView) setView("kitchen");
    } catch (e) { alert(fmtErr(e)); }
  };
  const paySplit = async (splits) => {
    // in POS flow this is only called from within Collect flow
    if (pendingCollect) return finishCollect("split", splits);
  };

  return (
    <div className="workspace pos">
      {pendingDraft && (
        <div className="draft-loaded-banner" data-testid="draft-loaded-banner">
          <b>Draft #{pendingDraft.draft_number}</b>
          <span>{pendingDraft.customer_name} · {pendingDraft.customer_phone}</span>
          <em>Review items and press "Place order &amp; collect payment" to finalise.</em>
          <button
            className="outline"
            data-testid="draft-loaded-dismiss"
            onClick={() => { setCart([]); clearPendingDraft?.(); }}
          >Discard draft</button>
        </div>
      )}
      <div className="page-head">
        <div>
          <p className="eyebrow">SERVICE FLOOR / {mode === "floor" ? "DINE-IN" : "TAKEAWAY"}</p>
          <h1>Good evening.</h1>
          <p className="muted">Route items across 15 kitchen stations · Dinner service</p>
        </div>
        <div className="head-actions">
          <div className="mode-switch" role="tablist" data-testid="order-mode-switch">
            <button
              data-testid="mode-dinein-button"
              role="tab"
              aria-selected={mode === "floor"}
              className={mode === "floor" ? "on" : ""}
              onClick={() => setMode("floor")}
            >Dine-in</button>
            <button
              data-testid="mode-takeaway-button"
              role="tab"
              aria-selected={mode === "counter"}
              className={mode === "counter" ? "on" : ""}
              onClick={() => setMode("counter")}
            >Takeaway</button>
          </div>
          {/* Table selector removed — tables are disabled in this build. */}
          <span className="live"><i />Live service</span>
        </div>
      </div>

      <div className="pos-grid">
        <section className="catalog">
          {topSelling.length > 0 && (
            <div className="top-selling-bar" data-testid="top-selling-bar">
              <span className="ts-eyebrow">TOP SELLERS</span>
              <div className="ts-list">
                {topSelling.map((t) => {
                  const item = menu.find((m) => m.id === t.menu_id);
                  const disabled = !item || item.effective_active === false;
                  return (
                    <button
                      key={t.menu_id}
                      data-testid={`top-selling-${t.menu_id}-button`}
                      className="ts-chip"
                      onClick={() => item && openItem(item)}
                      disabled={disabled}
                      title={disabled ? "Currently unavailable" : `Add ${t.name}`}
                    >{t.name}</button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="section-line menu-title">
            <h2>Menu</h2>
            <div className="menu-search">
              <input
                data-testid="menu-search-input"
                type="search"
                placeholder="Search menu…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button data-testid="menu-search-clear-button" className="search-clear" onClick={() => setSearch("")}>×</button>
              )}
            </div>
          </div>
          <div className="menu-grid">
            {filtered.length ? filtered.map((item) => {
              const hasChoices = (item.variants?.length || 0) > 0 || (item.addons?.length || 0) > 0;
              return (
                <button data-testid={`menu-item-${item.id}-button`} className="menu-item menu-item-noimg" key={item.id} onClick={() => openItem(item)}>
                  <div>
                    <b>{item.name}{hasChoices && <span className="mi-flag">customisable</span>}</b>
                    <small>{item.description}</small>
                    <strong>{money(item.price)} · {item.category}</strong>
                  </div>
                  <Plus className="add-icon" size={16} />
                </button>
              );
            }) : (
              <div data-testid="menu-no-results" className="empty" style={{ gridColumn: "1/-1", minHeight: 160 }}>
                <Utensils size={26} />
                <b>No items match “{search}”</b>
                <span>Try a different name, ingredient or category</span>
              </div>
            )}
          </div>

          {filteredAll.length > 0 && (
            <div className="pager" data-testid="pos-pager">
              <button
                data-testid="pos-prev-button"
                className="pager-btn"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
              >← Prev</button>
              <span data-testid="pos-page-info" className="pager-info">
                Page <b>{currentPage}</b> of {totalPages} · showing {filtered.length} of {filteredAll.length}
              </span>
              <select
                data-testid="pos-page-size-select"
                className="pager-btn pager-size"
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                title="Items per page"
              >
                <option value={20}>20 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
              <button
                data-testid="pos-next-button"
                className="pager-btn"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
              >Next →</button>
            </div>
          )}

          <div className="cat-strip" data-testid="pos-category-strip">
            <p className="eyebrow">CATEGORIES</p>
            <div className="categories">
              {cats.map((c) => (
                <button data-testid={`category-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-button`} className={category === c ? "chosen" : ""} onClick={() => setCategory(c)} key={c}>{c}</button>
              ))}
            </div>
          </div>
        </section>

        <aside className="ticket">
          <div className="ticket-head">
            <div>
              <p className="eyebrow">CURRENT TICKET</p>
              <h2>{mode === "counter" ? "Takeaway order" : "Dine-in order"}</h2>
            </div>
            <Receipt size={21} />
          </div>
          {cart.length ? (
            <div className="cart">
              {cart.map((x) => {
                const choice = describeChoice(x.variant, x.addons);
                const isEditingNote = noteFor === x.cartKey;
                return (
                  <div className="cart-row" key={x.cartKey}>
                    <div>
                      <b>{x.name}</b>
                      <small>{money(x.price)} · {x.category}</small>
                      {choice && <em className="cart-choice">{choice}</em>}
                      {x.note && !isEditingNote && <em className="cart-choice">Note: {x.note}</em>}
                      {isEditingNote && (
                        <div className="note-editor">
                          <input
                            data-testid={`note-input-${x.cartKey}`}
                            autoFocus
                            placeholder="e.g. no chilli, extra crispy"
                            value={noteDraft}
                            onChange={(e) => setNoteDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                setCart((c) => c.map((y) => (y.cartKey === x.cartKey ? { ...y, note: noteDraft.trim() || null } : y)));
                                setNoteFor(null);
                              } else if (e.key === "Escape") setNoteFor(null);
                            }}
                          />
                          <button data-testid={`note-save-${x.cartKey}`} className="outline" onClick={() => {
                            setCart((c) => c.map((y) => (y.cartKey === x.cartKey ? { ...y, note: noteDraft.trim() || null } : y)));
                            setNoteFor(null);
                          }}>Save</button>
                        </div>
                      )}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
                      <div className="stepper">
                        <button data-testid={`decrease-${x.cartKey}-button`} onClick={() => bumpLine(x.cartKey, -1)}><Minus size={13} /></button>
                        <span>{x.quantity}</span>
                        <button data-testid={`increase-${x.cartKey}-button`} onClick={() => bumpLine(x.cartKey, +1)}><Plus size={13} /></button>
                      </div>
                      <button data-testid={`note-toggle-${x.cartKey}`} className="note-btn"
                        onClick={() => { setNoteFor(isEditingNote ? null : x.cartKey); setNoteDraft(x.note || ""); }}>
                        <StickyNote size={12} />{x.note ? "Edit note" : "+ Note"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty">
              <Utensils size={26} />
              <b>No items yet</b>
              <span>Add dishes from the menu</span>
            </div>
          )}
          <div className="bill">
            <div><span>Subtotal</span><b>{money(subtotal)}</b></div>
            <div><span>Discount</span><b className="green">-{money(discount)}</b></div>
            {taxMode === "inter" ? (
              <div><span>IGST (5%)</span><b>{money(gstPreview)}</b></div>
            ) : (
              <>
                <div><span>CGST (2.5%)</span><b>{money(gstPreview / 2)}</b></div>
                <div><span>SGST (2.5%)</span><b>{money(gstPreview / 2)}</b></div>
              </>
            )}
            {Math.abs(roundingPreview) >= 0.005 && (
              <div data-testid="rounding-line"><span>Rounding</span><b className={roundingPreview >= 0 ? "" : "green"}>{roundingPreview >= 0 ? "+" : ""}{money(roundingPreview)}</b></div>
            )}
            <div className="grand"><span>Total</span><b data-testid="grand-total">{money(total)}</b></div>
          </div>
          <>
              <div className="discount-block" data-testid="discount-block">
                <label className="eyebrow" style={{ display: "block", marginBottom: 4 }}>DISCOUNT SLAB <span style={{ color: "var(--muted)", letterSpacing: 0, textTransform: "none", fontSize: 10 }}>· applied to each station individually</span></label>
                <select
                  data-testid="discount-slab-select"
                  className="filter-select"
                  style={{ width: "100%" }}
                  value={discountPercent}
                  onChange={(e) => {
                    const p = Number(e.target.value);
                    // Gate 30% Staff and 100% Owner slabs behind a 4-digit manager PIN — protects against misuse at the counter.
                    if (p >= 30 && p !== discountPercent) {
                      setPinAsk({ percent: p });
                      return;
                    }
                    setDiscountPercent(p);
                    const map = { 0: "", 5: "5% - Known Customer", 10: "10% - Special Customer", 15: "15% - Very Special Customer", 20: "20% - VIP", 30: "30% - Staff", 100: "100% - Owner" };
                    setDiscountReason(map[p] || "");
                  }}
                >
                  <option value={0}>No discount</option>
                  <option value={5}>5% — Known Customer</option>
                  <option value={10}>10% — Special Customer</option>
                  <option value={15}>15% — Very Special Customer</option>
                  <option value={20}>20% — VIP</option>
                  <option value={30}>30% — Staff</option>
                  <option value={100}>100% — Owner</option>
                </select>
                {discountPercent > 0 && (
                  <p className="muted" data-testid="discount-preview" style={{ fontSize: 11, marginTop: 6 }}>
                    Applying <b style={{ color: "#fff" }}>{discountPercent}%</b> across every station · saving <b style={{ color: "var(--red)" }}>-{money(discount)}</b> on {money(subtotal)}
                  </p>
                )}
              </div>
              <div className="skip-print-block" data-testid="skip-print-block" style={{ display: "flex", gap: 14, alignItems: "center", padding: "8px 12px", margin: "8px 0", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10 }}>
                <span className="eyebrow" style={{ color: "var(--muted)", margin: 0 }}>DO NOT PRINT</span>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer", fontSize: 13 }}>
                  <input data-testid="skip-kot-checkbox" type="checkbox" checked={skipKOT} onChange={(e) => setSkipKOT(e.target.checked)} />
                  KOT
                </label>
                <label style={{ display: "flex", gap: 6, alignItems: "center", cursor: "pointer", fontSize: 13 }}>
                  <input data-testid="skip-bill-checkbox" type="checkbox" checked={skipBill} onChange={(e) => setSkipBill(e.target.checked)} />
                  Bill
                </label>
              </div>
              <div className="two-actions" style={{ display: "flex", gap: 10, flexDirection: "row" }}>
                <button data-testid="collect-payment-button" className="primary gold"
                  style={{ flex: "1 1 auto", padding: "10px 14px", fontSize: 14 }}
                  disabled={submitting || !cart.length || (discountPercent > 0 && !discountReason.trim())} onClick={collectPayment}>
                  <CreditCard size={14} />{submitting ? "…" : "Collect Payment"}
                </button>
                <button data-testid="place-order-button" className="primary"
                  style={{ flex: "1 1 auto", padding: "10px 14px", fontSize: 14, background: "linear-gradient(180deg, #444, #2a2a2a)", color: "#fff" }}
                  disabled={submitting || !cart.length || (discountPercent > 0 && !discountReason.trim())} onClick={placeOrder}>
                  <Send size={14} />{submitting ? "Placing…" : "Place Order"}
                </button>
              </div>
            </>
        </aside>
      </div>

      {configuring && (
        <ItemConfigurator
          item={configuring}
          onCancel={() => setConfiguring(null)}
          onAdd={({ variant, addons, unitPrice, quantity, note }) => { addLine(configuring, variant, addons, unitPrice, quantity, note); setConfiguring(null); }}
        />
      )}
      {pendingCollect && !splitOpen && (
        <div className="modal-backdrop" onClick={(e) => e.target.classList.contains("modal-backdrop") && setPendingCollect(null)}>
          <div className="modal" style={{ width: "min(440px, 92vw)" }}>
            <div className="section-line">
              <h2>Collect payment</h2>
              <button data-testid="collect-close-button" onClick={() => setPendingCollect(null)}>×</button>
            </div>
            <p className="muted" style={{ fontSize: 12, margin: "0 0 4px" }}>
              {pendingCollect.table ? `Table ${pendingCollect.table}` : (pendingCollect.order_type === "table" ? "Dine-in" : "Takeaway")}{pendingCollect.token_number ? ` · Token T${String(pendingCollect.token_number).padStart(2, "0")}` : " · Token issued after payment"}
            </p>
            <div className="collect-total"><span>AMOUNT DUE</span><b>{money(pendingCollect.total)}</b></div>
            <div className="pay-row">
              <button data-testid="pay-cash-button" className="pay-btn" onClick={() => setCashOpen(true)}><Banknote size={16} />Cash</button>
              <button data-testid="pay-card-button" className="pay-btn" onClick={() => finishCollect("card")}><CreditCard size={16} />Card</button>
              <button data-testid="pay-upi-button" className="pay-btn" onClick={() => finishCollect("upi")}><Smartphone size={16} />UPI</button>
              <button data-testid="pay-split-button" className="pay-btn" onClick={() => setSplitOpen(true)}><Wallet size={16} />Split</button>
            </div>
          </div>
        </div>
      )}
      {splitOpen && pendingCollect && (
        <SplitModal
          total={pendingCollect.total}
          onCancel={() => setSplitOpen(false)}
          onSubmit={paySplit}
        />
      )}
      {cashOpen && pendingCollect && (
        <CashTenderModal
          total={pendingCollect.total}
          onCancel={() => setCashOpen(false)}
          onDone={async () => { setCashOpen(false); await finishCollect("cash"); }}
        />
      )}
      <ManagerPinModal
        open={!!pinAsk}
        percent={pinAsk?.percent}
        onCancel={() => setPinAsk(null)}
        onVerified={() => {
          const p = pinAsk.percent;
          setPinAsk(null);
          setDiscountPercent(p);
          const map = { 0: "", 5: "5% - Known Customer", 10: "10% - Special Customer", 15: "15% - Very Special Customer", 20: "20% - VIP", 30: "30% - Staff", 100: "100% - Owner" };
          setDiscountReason(map[p] || "");
        }}
      />
    </div>
  );
}

